package gr.tech.GRAttestation.components.modals

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.infrastructure.api.adminFetchAllStores
import gr.tech.GRAttestation.models.stores.STORE_STATUS
import gr.tech.GRAttestation.models.stores.StoreDTO
import gr.tech.GRAttestation.models.tables.BasicTableFilters
import gr.tech.GRAttestation.styles.GeneralInputLabelStyle
import gr.tech.GRAttestation.styles.bsInputLabelStyle
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

/*
    StorePickerSection
    ------------------
    Shared store selection UI used in add/edit modals.

    - lockedStoreId != null  →  fetches all stores, shows a single disabled row matched by id
    - lockedStoreId == null  →  fetches all stores, shows enabled radio list (all-stores page)
*/
@Composable
fun StorePickerSection(
    lockedStoreId: Int? = null,
    selectedStoreId: Int?,
    onStoreSelected: (Int) -> Unit,
) {
    var stores by remember { mutableStateOf<List<StoreDTO>>(emptyList()) }
    var isLoading by remember { mutableStateOf(false) }
    var loadError by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        isLoading = true
        adminFetchAllStores(
            pageNumber = 1,
            pageSize = 1000,
            storeStatus = STORE_STATUS.ACTIVE,
            basicFilters = BasicTableFilters(),
            onResponse = { response ->
                if (response.success && response.data != null) {
                    stores = response.data.items
                } else {
                    loadError = "Failed to load stores."
                }
                isLoading = false
            },
            onError = {
                loadError = "Error loading stores."
                isLoading = false
            }
        )
    }

    Div(attrs = Modifier.classNames("d-flex", "flex-column", "w-100").toAttrs()) {
        SpanText(
            text = "Store *",
            modifier = GeneralInputLabelStyle.toModifier().bsInputLabelStyle()
        )

        when {
            isLoading -> {
                Div(attrs = Modifier.classNames("text-muted", "small", "py-2").toAttrs()) {
                    Text("Loading stores...")
                }
            }

            loadError.isNotBlank() -> {
                Div(attrs = Modifier.classNames("alert", "alert-warning", "py-2").toAttrs()) {
                    Text(loadError)
                }
            }

            lockedStoreId != null -> {
                val lockedStore = stores.find { it.id == lockedStoreId }
                Div(attrs = Modifier.classNames("border", "rounded", "p-2").toAttrs()) {
                    Div(attrs = Modifier.classNames("d-flex", "align-items-center", "gap-2", "p-2").toAttrs()) {
                        Input(
                            type = InputType.Radio,
                            attrs = Modifier
                                .disabled(true)
                                .width(20.px).height(20.px)
                                .toAttrs {
                                    attr("name", "storeSelection")
                                    checked(true)
                                }
                        )
                        Span(attrs = Modifier.classNames("fw-bold").toAttrs()) {
                            Text(lockedStore?.storeName ?: "Store #$lockedStoreId")
                        }
                    }
                }
            }

            else -> {
                Div(
                    attrs = Modifier.classNames("border", "rounded", "p-2")
                        .maxHeight(250.px).overflow(Overflow.Hidden, Overflow.Auto)
                        .toAttrs()
                ) {
                    stores.forEach { store ->
                        Div(attrs = Modifier
                            .classNames("d-flex", "align-items-center", "gap-2", "p-2", "border-bottom")
                            .toAttrs()
                        ) {
                            Input(
                                type = InputType.Radio,
                                attrs = Modifier
                                    .width(20.px).height(20.px)
                                    .cursor(Cursor.Pointer)
                                    .id("store-${store.id}")
                                    .toAttrs {
                                        attr("name", "storeSelection")
                                        checked(selectedStoreId == store.id)
                                        onChange { onStoreSelected(store.id) }
                                    }
                            )
                            Label(
                                forId = "store-${store.id}",
                                attrs = Modifier.fillMaxWidth().cursor(Cursor.Pointer).toAttrs()
                            ) {
                                Span(attrs = Modifier.classNames("fw-bold").toAttrs()) {
                                    Text(store.storeName)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
